import qrcode from 'qrcode-generator';
import { CheckCircle2, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BROWSER_NAMES, detectDevice, installGuide, isTv, PLATFORM_NAMES, screensaverGuide, useInstallPrompt, type Guide, type Platform } from '../../lib/install';
import { buttonClass, Section } from './controls';

const OTHERS: Platform[] = ['windows', 'mac', 'iphone', 'ipad', 'android', 'chromeos', 'fire-tv', 'android-tv', 'samsung-tv', 'lg-tv'];

function Steps({ guide }: { guide: Guide }) {
  return (
    <>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm">
        {guide.steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
      {guide.note && <p className="muted text-xs">{guide.note}</p>}
    </>
  );
}

/** Settings → Install app: put KCL Canvas on this device as an app, and screensaver options. */
export function InstallTab() {
  const device = useMemo(() => detectDevice(navigator.userAgent, navigator.maxTouchPoints), []);
  const { canPrompt, installed, install } = useInstallPrompt();
  const [result, setResult] = useState<string | null>(null);
  const guide = installGuide(device);
  const url = window.location.origin;
  const qr = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(url);
    q.make();
    return q.createDataURL(4, 2);
  }, [url]);
  const detected = device.platform === 'other' ? 'this device' : `${PLATFORM_NAMES[device.platform]}${device.browser === 'other' ? '' : ` · ${BROWSER_NAMES[device.browser]}`}`;

  return (
    <>
      <Section title={guide.title} description={`Detected: ${detected}.`}>
        {installed ? (
          <p className="flex items-center gap-2 text-sm text-emerald-400">
            <CheckCircle2 className="h-5 w-5" aria-hidden /> KCL Canvas is running as an installed app on this device.
          </p>
        ) : (
          <>
            {guide.prompt && canPrompt && (
              <button
                type="button"
                className={buttonClass}
                onClick={() =>
                  void install().then((ok) => setResult(ok ? 'Installed – you’ll find KCL Canvas with your other apps.' : 'Not installed. You can install it any time from here.'))
                }
              >
                <Download className="h-4 w-4" aria-hidden /> Install KCL Canvas
              </button>
            )}
            {guide.prompt && !canPrompt && (
              <p className="muted text-xs">The install button appears here once {BROWSER_NAMES[device.browser]} offers it – or use its menu as below.</p>
            )}
            {result && (
              <p className="text-sm" role="status">
                {result}
              </p>
            )}
            <Steps guide={guide} />
          </>
        )}
      </Section>

      <Section title="Set up another device" description="Scan this with a phone or tablet to open KCL Canvas there, then follow the steps for that device.">
        <div className="flex items-center gap-4">
          <img src={qr} alt={`QR code for ${url}`} className="h-28 w-28 rounded bg-white p-1" />
          <span className="text-sm">{url.replace(/^https?:\/\//, '')}</span>
        </div>
        <div className="space-y-1">
          {OTHERS.filter((p) => p !== device.platform).map((p) => (
            <details key={p} className="rounded-lg border border-[var(--border)] px-3 py-2">
              <summary className="cursor-pointer text-sm">{PLATFORM_NAMES[p]}</summary>
              <div className="mt-2 space-y-2">
                <Steps guide={installGuide({ platform: p, browser: p === 'mac' || p === 'iphone' || p === 'ipad' ? 'safari' : 'chrome' })} />
              </div>
            </details>
          ))}
        </div>
      </Section>

      <Section
        title="Use as a screensaver"
        description="Browsers can’t make a website the system screensaver, so this needs a KCL Canvas app for each kind of device. Here’s what works today."
      >
        <Steps guide={screensaverGuide(device.platform)} />
        {!isTv(device.platform) && (
          <details className="rounded-lg border border-[var(--border)] px-3 py-2">
            <summary className="cursor-pointer text-sm">Other devices</summary>
            <div className="mt-2 space-y-3">
              {(['windows', 'mac', 'android-tv', 'fire-tv', 'ipad'] as Platform[])
                .filter((p) => p !== device.platform)
                .map((p) => {
                  const g = screensaverGuide(p);
                  return (
                    <div key={p} className="space-y-1">
                      <div className="text-sm font-medium">{g.title}</div>
                      <Steps guide={g} />
                    </div>
                  );
                })}
            </div>
          </details>
        )}
      </Section>
    </>
  );
}
